import type { InputProps } from '@chakra-ui/react'
import { Input, InputGroup, InputRightElement } from '@chakra-ui/react'
import React from 'react'
import { withFormControl, type FormFieldProps } from './hoc/withFormControl'

// Component-specific props
export interface InputFormControlComponentProps {
  type?: string
  placeholder?: string
  variant?: InputProps['variant']
  minLength?: number
  maxLength?: number
  htmlSize?: InputProps['htmlSize']
  autoComplete?: InputProps['autoComplete']
  pattern?: InputProps['pattern']
  onCopy?: InputProps['onCopy']
  rightElement?: React.ReactNode
  w?: InputProps['w']
  tabIndex?: number
  readOnly?: boolean
  size?: 'sm' | 'md' | 'lg'
}

// Base component that handles input logic
export const InputFormControlBase = React.forwardRef<
  HTMLInputElement,
  InputFormControlComponentProps & FormFieldProps<string | null, HTMLInputElement>
>((props, ref) => {
  const {
    // Form field props
    value,
    onChange,
    onBlur,
    isDisabled,
    name,
    // Component-specific props
    variant,
    type,
    htmlSize,
    placeholder,
    minLength,
    maxLength,
    onCopy,
    rightElement,
    w,
    pattern,
    autoComplete,
    tabIndex,
    readOnly,
    size,
    ...rest
  } = props

  return (
    <InputGroup>
      <Input
        ref={ref}
        name={name}
        value={value || ''}
        onChange={(e) => {
          const newValue = e.target.value
          // Transform empty string to null
          onChange(newValue === '' ? null : newValue)
        }}
        onBlur={onBlur}
        isDisabled={isDisabled}
        variant={variant}
        placeholder={placeholder}
        minLength={minLength}
        maxLength={maxLength}
        htmlSize={htmlSize}
        w={w}
        pattern={pattern}
        onCopy={onCopy}
        type={type}
        autoComplete={autoComplete}
        tabIndex={tabIndex}
        readOnly={readOnly}
        size={size}
        {...rest}
      />
      {rightElement && (
        <InputRightElement sx={{ w: 'auto', px: 4 }}>
          {rightElement}
        </InputRightElement>
      )}
    </InputGroup>
  )
})

;(InputFormControlBase as any).displayName = 'InputFormControlBase'

// Export the wrapped component
export const InputFormControl = withFormControl<InputFormControlComponentProps, string | null, HTMLInputElement>(InputFormControlBase)
